import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, Button, Input, Link } from "@mui/joy";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import { validators } from "../utils/validators";

const ForgetPassword: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  useEffect(() => {
    document.title = "Forget Password | Iskcon Kanpur";
  }, []);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldownSeconds((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handlePasswordReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const validation = validators.email(email);
      if (!validation.valid) {
        throw new Error(validation.error || "Invalid email format");
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (!response.ok) {
        let retryAfter = 0;
        let backendError = "Unable to process password reset request right now.";
        try {
          const data = await response.json();
          if (typeof data?.retry_after_seconds === "number") {
            retryAfter = Math.max(0, Math.floor(data.retry_after_seconds));
          }
          if (typeof data?.error === "string" && data.error.trim()) {
            backendError = data.error;
          }
        } catch {
          // no-op: use fallback message
        }

        if (retryAfter > 0) {
          setCooldownSeconds(retryAfter);
          throw new Error(`Too many attempts. Try again in ${retryAfter}s.`);
        }
        throw new Error(backendError);
      }

      setMessage("If an account exists for this email, a reset link has been sent.");
      setEmail("");
    } catch (err: any) {
      setError(err?.message || "Unable to process password reset request right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        textAlign: "center",
      }}
    >
      <Typography level="h2" fontSize="1.5rem" sx={{ mb: 2 }}>
        Reset Your Password
      </Typography>
      <Typography level="body-md" sx={{ mb: 4 }}>
        Enter your email address and we will send you a link to reset your
        password.
      </Typography>
      <form onSubmit={handlePasswordReset}>
        <FormControl required>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2, width: "300px" }}
          />
        </FormControl>
        <Button type="submit" variant="soft" color="primary" disabled={loading || cooldownSeconds > 0}>
          {loading ? "Sending..." : cooldownSeconds > 0 ? `Try again in ${cooldownSeconds}s` : "Send Reset Link"}
        </Button>
      </form>
      {message && (
        <Typography level="body-sm" sx={{ color: "green", mt: 2 }}>
          {message}
        </Typography>
      )}
      {error && (
        <Typography level="body-sm" sx={{ color: "red", mt: 2 }}>
          {error}
        </Typography>
      )}
      {cooldownSeconds > 0 && (
        <Typography level="body-sm" sx={{ color: "warning.500", mt: 1 }}>
          Please wait {cooldownSeconds}s before requesting another reset link.
        </Typography>
      )}
      <Typography level="body-sm" sx={{ mt: 3 }}>
        Remember your password? <Link href="/signin">Sign in</Link>
      </Typography>
    </Box>
  );
};

export default ForgetPassword;
